/**
 * Extracts a user-friendly error message from an unknown caught error.
 * Handles SwaggerException (NSwag) responses with JSON body parsing.
 * Use this in catch blocks instead of `catch (err: any)`.
 *
 * @example
 * ```ts
 * try { ... }
 * catch (error: unknown) {
 *   toast.error(getErrorMessage(error, 'Failed to save'));
 * }
 * ```
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
    // Handle SwaggerException (NSwag-generated API errors with response body)
    if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response: unknown }).response;
        if (response && typeof response === 'string') {
            try {
                const errorData = JSON.parse(response);
                if (errorData.detail) return errorData.detail;
                if (errorData.errors) {
                    const messages = Object.values(errorData.errors).flat().join('. ');
                    if (messages) return messages;
                }
                if (errorData.title) return errorData.title;
            } catch {
                return response;
            }
        }
    }
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return fallback;
}
